import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {Redirect, useHistory} from 'react-router-dom';
import {Breadcrumbs, FlexColumn, Link} from '@forecast-it/design-system';
import {CommitMutation} from '../../../../../mutations/CommitMutation';
import {TopHeaderBar} from '../../../../shared/components/headers/top-header-bar/TopHeaderBar';
import {useTrackPage} from '../../../../../tracking/amplitude/hooks/useTrackPage';
import type {EditBaselineTemplatePageQuery$data} from './__generated__/EditBaselineTemplatePageQuery.graphql';
import {BASELINE_TEMPLATES_PATH} from '../../templatesPaths';
import {isPlainClick} from '../../../../shared/util/LinkClickUtil';
import {BaselineTemplateForm, CommitTemplate} from '../form/BaselineTemplateForm';
import {
	fromDetail,
	heldDisabledCategories,
	heldDisabledRoles,
	Option,
	splitOptions,
	TemplateDetail,
} from '../form/baselineTemplateDraft';
import UpdateBaselineTemplateMutation from './UpdateBaselineTemplateMutation';

export const EditBaselineTemplatePageQuery = graphql`
	query EditBaselineTemplatePageQuery($id: ID!) {
		viewer {
			actualPersonId
			component(name: "settings_edit_baseline_template")
			company {
				id
				currency
				roles(first: 10000, includeDisabled: true) {
					edges {
						node {
							id
							name
							disabled
						}
					}
				}
				expenseCategories(first: 10000) {
					edges {
						node {
							id
							name
							disabled
						}
					}
				}
				baselineTemplates {
					id
					name
				}
				baselineTemplate(id: $id) {
					id
					name
					description
					includeDeliverables
					deliverables {
						id
						name
						description
					}
					phases {
						name
						startWeek
						durationDays
						deliverableIds
						timeEstimates {
							roleId
							minutes
							deliverableId
						}
						expenseEstimates {
							name
							expenseCategoryId
							revenue
							cost
							deliverableId
						}
					}
				}
			}
		}
	}
`;

type Options = {enabled: Option[]; disabled: Option[]};

type FormProps = {
	templateId: string;
	detail: TemplateDetail;
	roles: Options;
	categories: Options;
	currency: string;
	existingNames: readonly string[];
};

/**
 * Seeds the form from the stored template once. The route loads the page network-only, so the first render
 * carries the template as the server has it. Later renders, such as the store update a save makes, are ignored:
 * rebuilding the draft would give its rows new keys, which the form's state and the held options would no longer
 * match. Mount it with `key={templateId}` so another template starts afresh.
 */
const EditBaselineTemplateForm = ({templateId, detail, roles, categories, currency, existingNames}: FormProps) => {
	const [initialDraft] = useState(() => fromDetail(detail));
	const heldRoles = useMemo(() => heldDisabledRoles(initialDraft, roles.disabled), [initialDraft, roles]);
	const heldCategories = useMemo(() => heldDisabledCategories(initialDraft, categories.disabled), [initialDraft, categories]);

	// No automatic retry, as on create, so the two screens behave the same.
	const commitUpdate: CommitTemplate = (request, onSuccess, onError) =>
		CommitMutation(UpdateBaselineTemplateMutation, {id: templateId, ...request}, () => onSuccess(), false, null, onError);

	return (
		<BaselineTemplateForm
			roles={roles.enabled}
			categories={categories.enabled}
			currency={currency}
			existingNames={existingNames}
			initialDraft={initialDraft}
			editing
			heldRoles={heldRoles}
			heldCategories={heldCategories}
			commit={commitUpdate}
		/>
	);
};

type Props = {
	viewer: EditBaselineTemplatePageQuery$data['viewer'];
	/** The template's BaselineTemplate global id from the list, as the route decoded it. */
	templateId: string;
};

export const EditBaselineTemplatePage = ({viewer, templateId}: Props): JSX.Element | null => {
	useTrackPage('Edit Baseline Template');
	const {formatMessage} = useIntl();
	const history = useHistory();
	const company = viewer?.company;
	const detail = company?.baselineTemplate;

	const roles = useMemo(
		() =>
			splitOptions(
				(company?.roles?.edges ?? []).map(edge => edge?.node),
				name => formatMessage({id: 'baseline_templates.option.disabled'}, {name})
			),
		[company, formatMessage]
	);
	const categories = useMemo(
		() =>
			splitOptions(
				(company?.expenseCategories?.edges ?? []).map(edge => edge?.node),
				name => formatMessage({id: 'baseline_templates.option.disabled'}, {name})
			),
		[company, formatMessage]
	);
	// The template keeps its own name: only the other templates' names are in use.
	const existingNames = useMemo(
		() =>
			(company?.baselineTemplates ?? []).flatMap(template =>
				template?.name && template.id !== templateId ? [template.name] : []
			),
		[company, templateId]
	);

	if (!company) {
		return null;
	}
	if (!detail) {
		return <Redirect to="/not-found" />;
	}
	return (
		<FlexColumn gap="l">
			<Breadcrumbs>
				<Breadcrumbs.Item isDisabled>
					<span>{formatMessage({id: 'baseline_templates.breadcrumb.admin'})}</span>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					{/* The click goes through the router, so leaving with unsaved changes asks first, as Cancel does. */}
					<Link
						href={BASELINE_TEMPLATES_PATH}
						onClick={(event: React.MouseEvent) => {
							if (!isPlainClick(event)) {
								return;
							}
							event.preventDefault();
							history.push(BASELINE_TEMPLATES_PATH);
						}}
						data-cy="baseline-template-breadcrumb-templates"
					>
						{formatMessage({id: 'settings.templates.title'})}
					</Link>
				</Breadcrumbs.Item>
			</Breadcrumbs>
			<TopHeaderBar title={formatMessage({id: 'baseline_templates.edit_template'})} />
			<EditBaselineTemplateForm
				key={templateId}
				templateId={templateId}
				detail={detail}
				roles={roles}
				categories={categories}
				currency={company.currency ?? 'USD'}
				existingNames={existingNames}
			/>
		</FlexColumn>
	);
};
